// First-party page-event tracking — feeds the admin business analytics (visitor
// funnel + most-viewed rankings). Distinct from the Meta Pixel (lib/pixel.js),
// which is Meta-only and writes nothing we can query. Events are sent
// fire-and-forget via navigator.sendBeacon so they never block navigation, and
// every call is SSR/no-window safe.

const VISITOR_KEY = 'iamhero_visitor_id';

// Stable per-browser visitor id, persisted in localStorage. Used to count unique
// visitors and to attribute a generated preview back to the visitor that browsed
// the catalog (the Create form sends this id; the server stamps it on the Story).
export function getVisitorId() {
  if (typeof window === 'undefined') return null;
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = (window.crypto?.randomUUID?.()
        || `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`);
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    // localStorage blocked (private mode / cookies off) — degrade to no id.
    return null;
  }
}

const VALID_TYPES = new Set([
  'visit', 'template_view', 'category_view', 'promo_click', 'lang_switch',
  // Buy-funnel events (slug = story shareToken) — added to find where paying
  // customers drop off between a ready preview and a Stripe redirect.
  'preview_ready', 'preview_failed_screen', 'buy_click', 'checkout_view', 'checkout_submit',
  // Hesitation "need a hand?" offer on the unpaid preview: shown when the
  // detector fires, whatsapp when they tap through to contact us.
  'preview_help_shown', 'preview_help_whatsapp',
  // Pay-first A/B experiment funnel (see server routes/track.js for the meaning).
  'experiment_bucket', 'payfirst_add_to_cart', 'payfirst_checkout_view',
  'payfirst_checkout_submit', 'payfirst_create_story',
]);

// The pay-first cohort this browser is in RIGHT NOW, read synchronously from
// the same localStorage keys the experiment provider maintains (so it's correct
// even for events fired before the provider's async resolution — e.g. a
// checkout-view beacon on a hard refresh). 'pay_first_url' = forced via a
// ?preview=0 ad link; 'pay_first' = bucketed by the A/B flag; else 'control'.
export function payFirstVariant() {
  try {
    if (localStorage.getItem('iamhero_force_payfirst') === '1') return 'pay_first_url';
    return localStorage.getItem('iamhero_experiment_bucket') === 'pay_first' ? 'pay_first' : 'control';
  } catch {
    return 'control';
  }
}

// True while a staff member is signed in on this browser — same persistent
// flag lib/pixel.js maintains to keep the team's browsing out of Meta.
function isStaffBrowser() {
  try {
    return localStorage.getItem('iamhero_pixel_off') === '1';
  } catch {
    return false;
  }
}

// Records one page event. `type` is one of VALID_TYPES; slug/name identify the
// viewed template or category (omit for a plain visit). Best-effort: any failure
// is silently ignored — analytics must never affect the page. Pay-first funnel
// events are auto-tagged with the sender's cohort so the admin add-to-cart
// tracker can split ad traffic (?preview=0) from everything else — and are NOT
// sent at all from staff browsers, so the team's own testing never shows up in
// the cart stats.
export function trackEvent(type, { slug, name } = {}) {
  if (typeof window === 'undefined' || !VALID_TYPES.has(type)) return;
  if (type.startsWith('payfirst_') && isStaffBrowser()) return;
  const visitorId = getVisitorId();
  if (!visitorId) return;

  const payload = JSON.stringify({
    visitorId,
    type,
    slug: slug || null,
    name: name || null,
    path: window.location?.pathname || null,
    variant: type.startsWith('payfirst_') ? payFirstVariant() : null,
  });

  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon('/api/track', new Blob([payload], { type: 'application/json' }));
      return;
    }
  } catch {
    // fall through to fetch
  }
  // Fallback for browsers without sendBeacon. keepalive lets it survive an
  // unload (e.g. clicking straight through a card).
  try {
    fetch('/api/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* ignore */
  }
}
